/**
 * Contact Detail Drawer V3 - COMPREHENSIVE RELATIONAL PROFILE
 * 
 * ✅ Full relational data model rendering
 * ✅ Dropdown for field addition
 * ✅ PAMSSAC Lens minimizable/expandable
 * ✅ Timeline + Voice Journal integration
 * ✅ Decision Context header
 * ✅ Dark glass effect (no lightening)
 * ✅ Context Mode switcher
 */

import * as React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Mail,
  Phone,
  MessageSquare,
  Linkedin,
  Twitter,
  MapPin,
  Clock,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Copy,
  Target,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  Plus,
  ChevronDown,
  ChevronUp,
  Mic,
  Edit,
  Save,
  RefreshCw,
  Heart,
  Coffee,
  Plane,
  Users,
  Briefcase,
  Home,
  Award,
  BookOpen,
  Zap,
  Shield,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  RelationalProfile, 
  CONTEXT_MODES, 
  ContextMode,
  Interaction,
  DecisionContext 
} from '@/types/relationalProfile';

interface ContactDetailDrawerV3Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: RelationalProfile;
  ciScores: {
    ci: number;
    strength: number;
    relevance: number;
    influence: number;
    responsiveness: number;
    reciprocity: number;
    tier: 'A' | 'B' | 'C';
  };
  pamssacScores: {
    P: number;
    A1: number;
    M: number;
    S1: number;
    S2: number;
    A2: number;
    C: number;
    total: number;
    weakestTwo: Array<{
      dimension: string;
      score: number;
      why: string;
    }>;
  };
  timeline: Interaction[];
  decisionContext?: DecisionContext;
  evidence?: Array<{
    dimension: string;
    score: number;
    citations: Array<{
      messageId: string;
      snippet: string;
      date: string;
    }>;
  }>;
  onUpdateProfile?: (updates: Partial<RelationalProfile>) => Promise<void>;
  onExtractFromEmails?: () => Promise<void>;
  onSyncTimeline?: () => Promise<void>;
}

export function ContactDetailDrawerV3({
  open,
  onOpenChange,
  profile,
  ciScores,
  pamssacScores,
  timeline,
  decisionContext,
  evidence = [],
  onUpdateProfile,
  onExtractFromEmails,
  onSyncTimeline,
}: ContactDetailDrawerV3Props) {
  const [activeTab, setActiveTab] = React.useState('profile');
  const [isPamssacExpanded, setIsPamssacExpanded] = React.useState(false); // Collapsed by default as requested
  const [contextMode, setContextMode] = React.useState<ContextMode>('client');
  const [selectedField, setSelectedField] = React.useState<string>('');
  const [editingField, setEditingField] = React.useState<string | null>(null);
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
  
  // Extract fields from profile for display
  const allFields = [
    // Identity
    { category: 'identity', key: 'preferredName', label: 'Preferred Name', icon: Users, value: profile.identity.preferredName },
    { category: 'identity', key: 'pronunciation', label: 'Pronunciation', icon: MessageSquare, value: profile.identity.pronunciation },
    { category: 'identity', key: 'pronouns', label: 'Pronouns', icon: Users, value: profile.identity.pronouns },
    { category: 'identity', key: 'timezone', label: 'Timezone', icon: Clock, value: profile.identity.timezone },
    { category: 'identity', key: 'homeBase', label: 'Home Base', icon: MapPin, value: profile.identity.homeBase },
    { category: 'identity', key: 'languages', label: 'Languages', icon: Globe, value: profile.identity.languages?.join(', ') },
    
    // Contact
    { category: 'contact', key: 'phones', label: 'Phone', icon: Phone, value: profile.contact.phones?.[0] },
    { category: 'contact', key: 'messagingApps', label: 'Messaging', icon: MessageSquare, value: profile.contact.messagingApps?.map(m => `${m.app}: ${m.handle}`).join(', ') },
    
    // Background
    { category: 'background', key: 'company', label: 'Company', icon: Briefcase, value: profile.background?.company },
    { category: 'background', key: 'title', label: 'Title', icon: Award, value: profile.background?.title },
    { category: 'background', key: 'industry', label: 'Industry', icon: Briefcase, value: profile.background?.industry },
    { category: 'background', key: 'education', label: 'Education', icon: BookOpen, value: profile.background?.education?.join(', ') },
    
    // Preferences
    { category: 'preferences', key: 'diet', label: 'Dietary Preferences', icon: Coffee, value: profile.preferences?.foodDrink?.diet },
    { category: 'preferences', key: 'coffeeOrder', label: 'Coffee Order', icon: Coffee, value: profile.preferences?.foodDrink?.coffeeOrder },
    { category: 'preferences', key: 'hobbies', label: 'Hobbies', icon: Heart, value: profile.preferences?.hobbies?.join(', ') },
    { category: 'preferences', key: 'travelSeat', label: 'Travel Seat Preference', icon: Plane, value: profile.preferences?.travel?.seat },
    
    // Work Style
    { category: 'workStyle', key: 'decisionStyle', label: 'Decision Style', icon: Zap, value: profile.workStyle?.decisionStyle },
    { category: 'workStyle', key: 'meetingPrefs', label: 'Meeting Preferences', icon: Calendar, value: profile.workStyle?.meetingPrefs },
    
    // Household
    { category: 'household', key: 'partnerName', label: 'Partner Name', icon: Heart, value: profile.householdContext?.partnerName },
    { category: 'household', key: 'children', label: 'Children', icon: Users, value: profile.householdContext?.children?.map(c => c.name).join(', ') },
  ];

  const availableFields = allFields.filter(field => field.value);
  const [visibleFields, setVisibleFields] = React.useState<Set<string>>(
    new Set(availableFields.slice(0, 6).map(f => f.key))
  );

  const handleAddField = () => {
    if (selectedField && !visibleFields.has(selectedField)) {
      setVisibleFields(new Set([...visibleFields, selectedField]));
      setSelectedField('');
      toast.success('Field added');
    }
  };

  const handleSaveField = async (fieldKey: string, category: string) => {
    const value = fieldValues[fieldKey];
    if (!value || !onUpdateProfile) return;

    try {
      const updates: any = {};
      updates[category] = {
        ...profile[category as keyof RelationalProfile],
        [fieldKey]: value,
      };
      
      await onUpdateProfile(updates);
      setEditingField(null);
      toast.success('Field saved');
    } catch (error) {
      toast.error('Failed to save field');
    }
  };

  const getInitials = () => {
    const name = profile.identity.fullName || profile.contact.emails?.[0];
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'A': return 'text-[#D4AF37]';
      case 'B': return 'text-[#008080]';
      case 'C': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getTrendIcon = (score: number) => {
    if (score >= 75) return <TrendingUp className="w-3 h-3 text-[#008080]" />;
    if (score <= 40) return <TrendingDown className="w-3 h-3 text-destructive" />;
    return <Minus className="w-3 h-3 text-muted-foreground" />;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'meeting': return <Calendar className="w-4 h-4" />;
      case 'call': return <Phone className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const voiceJournals = timeline.filter(item => item.channel === 'call');

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={true}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-3xl p-0 glass-card border-border/50 overflow-hidden flex flex-col max-h-screen z-[100]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <SheetHeader className="p-4 sm:p-6 pb-0 space-y-3 sm:space-y-4">
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Avatar */}
            <Avatar className="h-12 w-12 sm:h-16 sm:w-16 ring-2 ring-primary/20">
              <AvatarFallback className="text-lg sm:text-2xl font-semibold bg-gradient-to-br from-primary/20 to-accent/20">
                {getInitials()}
              </AvatarFallback>
            </Avatar>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <SheetTitle className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text ci-gradient-gold mb-1">
                    {profile.identity.fullName}
                  </SheetTitle>
                  {profile.identity.preferredName && profile.identity.preferredName !== profile.identity.fullName && (
                    <p className="text-xs text-muted-foreground">({profile.identity.preferredName})</p>
                  )}
                  {profile.background?.title && (
                    <p className="text-xs sm:text-sm text-muted-foreground">{profile.background.title}</p>
                  )}
                  {profile.background?.company && (
                    <p className="text-xs sm:text-sm font-medium">{profile.background.company}</p>
                  )}
                </div>
              </div>

              {/* Meta Row */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="truncate max-w-[150px] sm:max-w-[200px]">{profile.contact.emails?.[0] || 'No email'}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                    onClick={() => {
                      if (profile.contact.emails?.[0]) {
                        navigator.clipboard.writeText(profile.contact.emails[0]);
                        toast.success('Email copied');
                      }
                    }}
                  >
                    <Copy className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  </Button>
                </div>
                {profile.identity.timezone && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{profile.identity.timezone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Context Mode Switcher */}
          <Card className="glass-card border-border/50 p-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Relationship Mode:</label>
              <Select value={contextMode} onValueChange={(v: ContextMode) => setContextMode(v)}>
                <SelectTrigger className="w-[200px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONTEXT_MODES).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-xs">
                      {config.purpose}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Decision Context Header */}
          {decisionContext && (
            <Card className="glass-card border-border/50 p-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                {decisionContext.owner && (
                  <div>
                    <p className="text-muted-foreground mb-1">Owner</p>
                    <p className="font-semibold">{decisionContext.owner}</p>
                  </div>
                )}
                {decisionContext.objective && (
                  <div>
                    <p className="text-muted-foreground mb-1">Objective</p>
                    <p className="font-semibold">{decisionContext.objective}</p>
                  </div>
                )}
                {decisionContext.valueAtStake && (
                  <div>
                    <p className="text-muted-foreground mb-1">Value at Stake</p>
                    <p className="font-semibold text-[#D4AF37]">${decisionContext.valueAtStake.toLocaleString()}</p>
                  </div>
                )}
                {decisionContext.stage && (
                  <div>
                    <p className="text-muted-foreground mb-1">Stage</p>
                    <Badge variant="outline" className="mt-1">{decisionContext.stage}</Badge>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* CI Scores with Trends */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Badge className={`${getTierColor(ciScores.tier)} glass-card border-border/50 text-sm sm:text-lg px-3 py-1 sm:px-4 sm:py-2 flex items-center gap-2`}>
              CI {ciScores.tier} • {ciScores.ci}
              {getTrendIcon(ciScores.ci)}
            </Badge>
            <Badge className={`${getTierColor(pamssacScores.total >= 80 ? 'A' : pamssacScores.total >= 60 ? 'B' : 'C')} glass-card border-border/50 text-sm sm:text-lg px-3 py-1 sm:px-4 sm:py-2`}>
              PAMSSAC {pamssacScores.total}
            </Badge>
            {decisionContext?.benchmark && (
              <Badge variant="outline" className="text-xs">
                vs median: {decisionContext.benchmark.tierMedian} (Δ{pamssacScores.total - decisionContext.benchmark.tierMedian})
              </Badge>
            )}
          </div>
        </SheetHeader>

        <Separator className="opacity-10" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-3 sm:mx-6 grid grid-cols-4 glass-card border-border/50 text-xs sm:text-sm">
            <TabsTrigger value="profile" className="text-xs sm:text-sm">Profile</TabsTrigger>
            <TabsTrigger value="timeline" className="text-xs sm:text-sm">Timeline</TabsTrigger>
            <TabsTrigger value="pamssac" className="text-xs sm:text-sm">PAMSSAC</TabsTrigger>
            <TabsTrigger value="insights" className="text-xs sm:text-sm">Insights</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 px-3 sm:px-6">
            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-4 mt-4">
              {/* AI Analysis & Insights - Show database fields */}
              {((profile as any).aiSummary || (profile as any).whyImportant) && (
                <Card className="glass-card border-border/50 border-2 border-[#D4AF37]/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
                      AI Intelligence Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {((profile as any).aiSummary) && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground mb-1 block">AI Summary</label>
                        <p className="leading-relaxed">{(profile as any).aiSummary}</p>
                      </div>
                    )}
                    {((profile as any).whyImportant) && (
                      <div className="p-3 rounded-lg glass-card border-border/50 bg-primary/5">
                        <label className="text-xs font-semibold text-[#D4AF37] mb-1 block">Why Important</label>
                        <p>{(profile as any).whyImportant}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Next Best Action from DB */}
              {((profile as any).suggestedActionType || (profile as any).suggestedActionReason) && (
                <Card className="glass-card border-border/50 border-2 border-[#008080]/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#008080]" />
                      Next Best Action
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {((profile as any).suggestedActionType) && (
                      <Badge className="bg-[#008080] text-white">{(profile as any).suggestedActionType}</Badge>
                    )}
                    {((profile as any).suggestedActionReason) && (
                      <p>{(profile as any).suggestedActionReason}</p>
                    )}
                    {((profile as any).suggestedActionTemplate) && (
                      <div className="p-3 rounded-lg glass-card border-border/50 bg-secondary/10">
                        <label className="text-xs font-semibold text-muted-foreground mb-1 block">Suggested Message</label>
                        <p className="italic">{(profile as any).suggestedActionTemplate}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Goals, Needs, Wants from DB */}
              {((profile as any).goalsTop5 || (profile as any).needsTop5 || (profile as any).wantsTop5) && (
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#D4AF37]" />
                      Relationship Intelligence
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    {((profile as any).goalsTop5) && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground mb-2 block">Their Goals</label>
                        <div className="whitespace-pre-wrap p-2 rounded glass-card border-border/50">
                          {(profile as any).goalsTop5}
                        </div>
                      </div>
                    )}
                    {((profile as any).needsTop5) && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground mb-2 block">Their Needs</label>
                        <div className="whitespace-pre-wrap p-2 rounded glass-card border-border/50">
                          {(profile as any).needsTop5}
                        </div>
                      </div>
                    )}
                    {((profile as any).wantsTop5) && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground mb-2 block">Their Wants</label>
                        <div className="whitespace-pre-wrap p-2 rounded glass-card border-border/50">
                          {(profile as any).wantsTop5}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Relational Profile</span>
                    {onExtractFromEmails && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={onExtractFromEmails}
                        className="text-xs"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Extract from Emails
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Dropdown to add fields */}
                  <div className="flex items-center gap-2">
                    <Select value={selectedField} onValueChange={setSelectedField}>
                      <SelectTrigger className="flex-1 glass-card border-border/50">
                        <SelectValue placeholder="Add a field..." />
                      </SelectTrigger>
                      <SelectContent>
                        {allFields
                          .filter(field => !visibleFields.has(field.key) && field.value)
                          .map(field => (
                            <SelectItem key={field.key} value={field.key}>
                              {field.label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleAddField}
                      disabled={!selectedField}
                      className="ci-gradient-gold text-[#0a0a0b]"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Display visible fields */}
                  <div className="space-y-3">
                    {Array.from(visibleFields).map(fieldKey => {
                      const field = allFields.find(f => f.key === fieldKey);
                      if (!field || !field.value) return null;

                      const isEditing = editingField === fieldKey;
                      const Icon = field.icon;

                      return (
                        <div key={fieldKey} className="glass-card border-border/50 p-3 rounded-lg">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <label className="text-xs font-medium text-muted-foreground">
                                {field.label}
                              </label>
                            </div>
                            {onUpdateProfile && (
                              isEditing ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => handleSaveField(fieldKey, field.category)}
                                >
                                  <Save className="w-3 h-3" />
                                </Button>
                              ) : (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => setEditingField(fieldKey)}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                              )
                            )}
                          </div>
                          {isEditing ? (
                            <Input
                              value={fieldValues[fieldKey] || field.value}
                              onChange={(e) => setFieldValues({ ...fieldValues, [fieldKey]: e.target.value })}
                              className="text-sm"
                            />
                          ) : (
                            <p className="text-sm">{field.value}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Milestones */}
                  {profile.milestones && profile.milestones.length > 0 && (
                    <Card className="glass-card border-border/50">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Milestones
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {profile.milestones.map((milestone, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded glass-card border-border/50">
                            <span className="text-sm">{milestone.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(milestone.date), 'MMM d, yyyy')}
                            </Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Commitments */}
                  {profile.commitments && profile.commitments.length > 0 && (
                    <Card className="glass-card border-border/50">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Open Commitments
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {profile.commitments.filter(c => c.status !== 'completed').map((commitment, idx) => (
                          <div key={idx} className="p-2 rounded glass-card border-border/50">
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-sm font-medium">{commitment.description}</p>
                              <Badge 
                                variant={commitment.status === 'overdue' ? 'destructive' : 'default'}
                                className="text-xs"
                              >
                                {commitment.status}
                              </Badge>
                            </div>
                            {commitment.dueDate && (
                              <p className="text-xs text-muted-foreground">
                                Due: {format(new Date(commitment.dueDate), 'MMM d, yyyy')}
                              </p>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Timeline Tab - Email & Calendar with Journal Entries */}
            <TabsContent value="timeline" className="space-y-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold">Email, Calendar & Journal Timeline</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {timeline.length} total interactions • {timeline.filter(e => e.channel === 'email').length} emails • {timeline.filter(e => e.channel === 'meeting').length} meetings • {timeline.filter(e => e.channel === 'call').length} notes
                  </p>
                </div>
                {onSyncTimeline && (
                  <Button size="sm" variant="outline" onClick={onSyncTimeline}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Sync
                  </Button>
                )}
              </div>

              {/* Email Timeline Section - ALWAYS VISIBLE with lazy loading */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Mail className="w-4 h-4 text-[#008080]" />
                    Email History ({timeline.filter(e => e.channel === 'email').length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                  {timeline.filter(e => e.channel === 'email').length === 0 ? (
                    <div className="text-center py-6 space-y-2">
                      <Mail className="w-10 h-10 mx-auto text-muted-foreground opacity-20" />
                      <p className="text-sm text-muted-foreground font-medium">
                        No email history found
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Email exchanges with this contact will appear here automatically
                      </p>
                    </div>
                  ) : (
                    timeline.filter(e => e.channel === 'email').map((event) => (
                      <div key={event.id} className="glass-card border-border/50 p-3 rounded-lg hover:border-primary/30 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-2 rounded-lg glass-card border-border/50">
                            <Mail className="w-4 h-4 text-[#008080]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-medium text-sm">{event.topic || 'Email Conversation'}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                            {event.evidenceSnippet && (
                              <div className="mt-2 p-2 rounded glass-card border-border/50 text-xs text-muted-foreground">
                                "{event.evidenceSnippet}"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Calendar Timeline Section - ALWAYS VISIBLE with lazy loading */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    Meeting History ({timeline.filter(e => e.channel === 'meeting').length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                  {timeline.filter(e => e.channel === 'meeting').length === 0 ? (
                    <div className="text-center py-6 space-y-2">
                      <Calendar className="w-10 h-10 mx-auto text-muted-foreground opacity-20" />
                      <p className="text-sm text-muted-foreground font-medium">
                        No meeting history found
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Calendar events with this contact will appear here automatically
                      </p>
                    </div>
                  ) : (
                    timeline.filter(e => e.channel === 'meeting').map((event) => (
                      <div key={event.id} className="glass-card border-border/50 p-3 rounded-lg hover:border-primary/30 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-2 rounded-lg glass-card border-border/50">
                            <Calendar className="w-4 h-4 text-[#D4AF37]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-medium text-sm">{event.topic || 'Meeting'}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                            {event.evidenceSnippet && (
                              <div className="mt-2 p-2 rounded glass-card border-border/50 text-xs text-muted-foreground">
                                "{event.evidenceSnippet}"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Voice Journals Section - ALWAYS VISIBLE with lazy loading */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Mic className="w-4 h-4 text-[#D4AF37]" />
                    Voice Journal Entries ({voiceJournals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                  {voiceJournals.length === 0 ? (
                    <div className="text-center py-6 space-y-2">
                      <Mic className="w-10 h-10 mx-auto text-muted-foreground opacity-20" />
                      <p className="text-sm text-muted-foreground font-medium">
                        No voice journal entries yet
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Click the "Voice Journal" button in the toolbar to record notes about this contact
                      </p>
                    </div>
                  ) : (
                    voiceJournals.map((journal) => (
                      <div key={journal.id} className="p-3 rounded glass-card border-border/50 hover:border-primary/30 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-medium">{journal.topic || 'Voice Note'}</p>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(journal.date), 'MMM d')}
                          </span>
                        </div>
                        {journal.evidenceSnippet && (
                          <p className="text-xs text-muted-foreground italic">
                            "{journal.evidenceSnippet}"
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* All Interactions Consolidated View - ALWAYS VISIBLE with lazy loading */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-base">All Interactions Timeline ({timeline.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                  {timeline.length === 0 ? (
                    <div className="text-center py-8 space-y-3">
                      <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground opacity-30" />
                      <div>
                        <p className="text-sm text-muted-foreground font-medium mb-1">
                          No timeline events found
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Timeline will populate automatically as you interact with this contact via email, calendar, and voice notes
                        </p>
                      </div>
                      <div className="flex flex-wrap justify-center gap-2 pt-2">
                        <Badge variant="outline" className="text-xs">
                          <Mail className="w-3 h-3 mr-1" />
                          Emails
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          Meetings
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <Mic className="w-3 h-3 mr-1" />
                          Journals
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    timeline.map((event) => (
                      <div key={event.id} className="glass-card border-border/50 p-3 rounded-lg hover:border-primary/30 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-2 rounded-lg glass-card border-border/50">
                            {getTypeIcon(event.channel)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{event.topic || 'Interaction'}</p>
                                <Badge variant="outline" className="text-xs">
                                  {event.channel}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                            {event.evidenceSnippet && (
                              <div className="mt-2 p-2 rounded glass-card border-border/50 text-xs text-muted-foreground">
                                "{event.evidenceSnippet}"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* PAMSSAC Tab - Collapsible */}
            <TabsContent value="pamssac" className="space-y-4 mt-4">
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">PAMSSAC Framework</CardTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsPamssacExpanded(!isPamssacExpanded)}
                    >
                      {isPamssacExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Minimize
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          Expand
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="text-xs space-y-2">
                  <p className="text-muted-foreground">
                    <strong>PAMSSAC:</strong> Placement • Access • Motivation • Suitability • Susceptibility • Accessibility • Commonalities
                  </p>
                  {isPamssacExpanded && (
                    <p className="text-muted-foreground">
                      Measures relationship strength across 7 dimensions. Each dimension scored 0-100. Click to view evidence and transparency.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* PAMSSAC Dimensions */}
              <Collapsible open={isPamssacExpanded} onOpenChange={setIsPamssacExpanded}>
                <CollapsibleContent>
                  <div className="space-y-3">
                    {[
                      { key: 'P', label: 'Placement', score: pamssacScores.P, definition: 'Power to decide/influence', weight: 0.15 },
                      { key: 'A1', label: 'Access', score: pamssacScores.A1, definition: 'Warm paths & channels', weight: 0.15 },
                      { key: 'M', label: 'Motivation', score: pamssacScores.M, definition: 'Energy to engage now', weight: 0.20 },
                      { key: 'S1', label: 'Suitability', score: pamssacScores.S1, definition: 'ICP & problem fit', weight: 0.15 },
                      { key: 'S2', label: 'Susceptibility', score: pamssacScores.S2, definition: 'Openness to change', weight: 0.15 },
                      { key: 'A2', label: 'Accessibility', score: pamssacScores.A2, definition: 'Practical reachability', weight: 0.10 },
                      { key: 'C', label: 'Commonalities', score: pamssacScores.C, definition: 'Affinity bridges', weight: 0.10 },
                    ].map(dim => {
                      const evidenceForDim = evidence.find(e => e.dimension.toUpperCase().startsWith(dim.key));
                      const topEvidence = evidenceForDim?.citations.slice(0, 3) || [];
                      
                      return (
                        <Card key={dim.key} className="glass-card border-border/50">
                          <Collapsible>
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold">{dim.label}</h4>
                                      <Badge variant="outline" className="text-xs">
                                        Weight: {(dim.weight * 100).toFixed(0)}%
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground">{dim.definition}</p>
                                  </div>
                                  <div className="text-2xl font-bold text-transparent bg-clip-text ci-gradient-gold">
                                    {dim.score}
                                  </div>
                                </div>
                                
                                {/* Progress Bar */}
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                                    style={{ width: `${dim.score}%` }}
                                  />
                                </div>

                                {/* Evidence */}
                                {topEvidence.length > 0 && (
                                  <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between p-2 rounded hover:bg-secondary/20 cursor-pointer">
                                      <span className="text-xs font-medium">View Evidence ({topEvidence.length} signals)</span>
                                      <ChevronDown className="w-4 h-4" />
                                    </div>
                                  </CollapsibleTrigger>
                                )}
                                
                                <CollapsibleContent>
                                  <div className="space-y-2 mt-2">
                                    {topEvidence.map((cite, idx) => (
                                      <div key={idx} className="p-2 rounded glass-card border-border/50 text-xs">
                                        <div className="flex items-start justify-between mb-1">
                                          <span className="text-muted-foreground">Evidence {idx + 1}</span>
                                          <span className="text-muted-foreground">
                                            {format(new Date(cite.date), 'MMM d, yyyy')}
                                          </span>
                                        </div>
                                        <p className="text-muted-foreground italic">"{cite.snippet}"</p>
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </div>
                            </CardContent>
                          </Collapsible>
                        </Card>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="space-y-4 mt-4">
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
                    AI Narrative
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {profile.derivedSignals?.healthScore && profile.derivedSignals.healthScore > 75
                      ? "Strong, healthy relationship with high engagement and mutual value exchange."
                      : "Relationship shows potential for deepening with targeted touchpoints and value delivery."}
                  </p>
                </CardContent>
              </Card>

              {/* Data Quality */}
              {profile.dataQuality && (
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Shield className="w-4 h-4" />
                      Data Quality
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Confidence</span>
                      <Badge variant="outline">
                        {((profile.dataQuality.confidence || 0) * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Source</span>
                      <span className="text-sm">{profile.dataQuality.sourceOfTruth}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Last Verified</span>
                      <span className="text-sm">
                        {profile.dataQuality.lastVerified ? format(new Date(profile.dataQuality.lastVerified), 'MMM d, yyyy') : 'Never'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
